import React, { useState, useEffect, useCallback } from 'react';
import './BusinessSelector.css';

interface Business {
  id: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  country_code: string;
  base_currency_code: string;
  role: string;
  joined_at: string;
  last_accessed: string;
  last_selected_at: string;
  is_default: boolean;
}

interface BusinessSelectorProps {
  onBusinessSelect: (business: Business) => void;
  currentBusiness?: Business;
  variant?: 'default' | 'profile';
}

const BusinessSelector: React.FC<BusinessSelectorProps> = ({
  onBusinessSelect,
  currentBusiness,
  variant = 'default',
}) => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSelector, setShowSelector] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(currentBusiness || null);

  const fetchUserBusinesses = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await fetch('http://localhost:5001/api/business/user-businesses', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch businesses');
      }

      const data = await response.json();
      if (data.success) {
        const bizList = data.data.businesses;
        setBusinesses(bizList);

        // Select default silently (no callback)
        if (!currentBusiness && bizList.length > 0) {
          const defaultBusiness =
            bizList.find((b: Business) => b.is_default) || bizList[0];
          setSelectedBusiness(defaultBusiness); // ✅ only local state
        }
      } else {
        setError(data.error || 'Failed to fetch businesses');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentBusiness]);

  useEffect(() => {
    fetchUserBusinesses();
  }, [fetchUserBusinesses]);

  const handleBusinessSelect = (business: Business) => {
    setSelectedBusiness(business);
    setShowSelector(false);
    onBusinessSelect(business); // ✅ only fires on user click
  };

  const setAsDefault = async (business: Business) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`http://localhost:5001/api/business/set-default`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ businessId: business.id }),
      });

      if (response.ok) {
        setBusinesses(prev =>
          prev.map(b => ({
            ...b,
            is_default: b.id === business.id,
          }))
        );
      }
    } catch (err) {
      console.error('Error setting default business:', err);
    }
  };

  const handleCreateBusiness = () => {
    // Navigate to business management page
    window.location.href = '/business-management';
  };

  if (loading) {
    return (
      <div className="business-selector">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <span>Loading businesses...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="business-selector">
        <div className="error-message">
          <span className="material-icons">error</span>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (businesses.length === 0) {
    return (
      <div className="business-selector">
        <div className="no-businesses">
          <span className="material-icons">business</span>
          <span>No businesses found</span>
          <button 
            className="btn btn-primary create-business-btn"
            onClick={handleCreateBusiness}
          >
            <span className="material-icons">add</span>
            Create Business
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`business-selector${variant === 'profile' ? ' business-selector--profile' : ''}`}>
      <div
        className="current-business"
        onClick={() => setShowSelector(!showSelector)}
        role="button"
        tabIndex={0}
        aria-expanded={showSelector}
        aria-haspopup="listbox"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowSelector(!showSelector);
          }
        }}
      >
        <div className="business-info">
          <span className="material-icons">business</span>
          <div className="business-details">
            <div className="business-name">
              {selectedBusiness?.name || 'Select Business'}
            </div>
            <div className="business-role">
              {selectedBusiness?.role || ''}
            </div>
          </div>
        </div>
        <span className="material-icons dropdown-icon">
          {showSelector ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
        </span>
      </div>

      {showSelector && (
        <div className="business-dropdown">
          <div className="dropdown-header">
            <span>Select Business</span>
            <span className="business-count">({businesses.length})</span>
          </div>

          <div className="business-list">
            {businesses.map(business => (
              <div
                key={business.id}
                className={`business-item ${selectedBusiness?.id === business.id ? 'selected' : ''}`}
                onClick={() => handleBusinessSelect(business)}
              >
                <div className="business-item-info">
                  <div className="business-item-name">
                    {business.name}
                    {business.is_default && <span className="default-badge">Default</span>}
                  </div>
                  <div className="business-item-details">
                    <span className="role">{business.role}</span>
                    <span className="currency">{business.base_currency_code}</span>
                  </div>
                </div>

                <div className="business-item-actions">
                  {!business.is_default && (
                    <button
                      className="set-default-btn"
                      onClick={e => {
                        e.stopPropagation();
                        setAsDefault(business);
                      }}
                      title="Set as default"
                    >
                      <span className="material-icons">star_border</span>
                    </button>
                  )}

                  {business.is_default && (
                    <span className="default-icon" title="Default business">
                      <span className="material-icons">star</span>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="dropdown-footer">
            <button 
              className="create-business-btn"
              onClick={handleCreateBusiness}
            >
              <span className="material-icons">add</span>
              Create New Business
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessSelector;
